// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
// <GetUserSnippet>
import {
  Client,
  GraphRequestOptions,
  PageCollection,
  PageIterator
} from '@microsoft/microsoft-graph-client'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { endOfWeek, startOfWeek } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { User, Event } from '@microsoft/microsoft-graph-types'
import { Calendars } from 'types/ms_outlook'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import Logger from 'services/Logger'

let graphClient: Client | undefined = undefined

function ensureClient(authProvider: AuthCodeMSALBrowserAuthenticationProvider) {
  if (!graphClient) {
    graphClient = Client.initWithMiddleware({
      authProvider: authProvider
    })
  }

  return graphClient
}

export async function getUser(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider
): Promise<User> {
  ensureClient(authProvider)

  // Return the /me API endpoint result as a User object
  const user: User = await graphClient
    ?.api('/me')
    // Only retrieve the specific fields needed
    .select('displayName,mail,mailboxSettings,userPrincipalName')
    .get()

  return user
}
// </GetUserSnippet>

export async function getUserCalendars(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider
): Promise<any> {
  ensureClient(authProvider)

  // Return the /me API endpoint result as a User object
  const calendars: Calendars = await graphClient?.api('/me/calendars').get()

  return calendars.value
}

// <GetUserWeekCalendarSnippet>
export async function getUserWeekCalendar(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  timeZone: string,
  calendarId: string | undefined = undefined,
  from: string,
  to: string
): Promise<Event[]> {
  ensureClient(authProvider)

  let response: PageCollection = await graphClient
    ?.api(
      calendarId
        ? `/me/calendars/${calendarId}/calendarview`
        : '/me/calendarview'
    )
    .header('Prefer', `outlook.timezone="${timeZone}"`)
    .query({ startDateTime: from, endDateTime: to })

    .orderby('start/dateTime')
    .top(25)
    .get()

  if (response['@odata.nextLink']) {
    // Presence of the nextLink property indicates more results are available
    // Use a page iterator to get all results
    let events: Event[] = []

    // Must include the time zone header in page
    // requests too
    let options: GraphRequestOptions = {
      headers: { Prefer: `outlook.timezone="${timeZone}"` }
    }

    let pageIterator = new PageIterator(
      graphClient!,
      response,
      (event) => {
        events.push(event)
        return true
      },
      options
    )

    await pageIterator.iterate()

    return events
  } else {
    return response.value
  }
}
// </GetUserWeekCalendarSnippet>

// <CreateEventSnippet>
export async function createEvent(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  newEvent: Event,
  calendarId: string
): Promise<Event> {
  ensureClient(authProvider)

  // POST /me/events
  // JSON representation of the new event is sent in the
  // request body
  return await graphClient
    ?.api(`/me/calendars/${calendarId}/events`)
    .post(newEvent)
}
// </CreateEventSnippet>

export async function getEventDetailsById(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  eventId: string
): Promise<Event | undefined> {
  ensureClient(authProvider)

  const event = await graphClient?.api(`/me/events/${eventId}`).get()
  return event
}

export async function updateEvent(
  eventId: string,
  updatedEvent: Event
): Promise<Event> {
  try {
    const result = await graphClient
      ?.api(`/me/events/${eventId}`)
      .update(updatedEvent)
    console.log('Event updated successfully', result)
    return result
  } catch (error) {
    console.error('Error updating event', error)
    throw error
  }
}

export async function updateAttendeeStatus(
  status: string,
  eventId: string
): Promise<void> {
  try {
    let type = 'accept'

    if (status === 'declined') {
      type = 'decline'
    } else if (status === 'tentativelyAccepted') {
      type = 'tentativelyAccept'
    }

    const result = await graphClient
      ?.api(`/me/calendar/events/${eventId}/${type}`)
      .post({ sendResponse: true })
    console.log(result)

    console.log('Event status successfully changed to ', status)
  } catch (error) {
    console.error('Error updating event', error)
    throw error
  }
}

export async function deleteEvent(eventId: string): Promise<Event> {
  try {
    const result = await graphClient?.api(`/me/events/${eventId}`).delete()
    console.log('Event updated successfully', result)
    return result
  } catch (error) {
    console.error('Error updating event', error)
    throw error
  }
}
